import React, { useContext } from 'react'
import {
  Container,
  Row,
  Col,
  Table,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { ABSpinner, NotificationContext } from '@abroad/components'
import { StaffCoachIcon } from '../assetsComponents'
import { formatDate } from '../../utils/date'

// Every setup item the Calendar Setup column can report as missing.
// Red cross is shown only when all of them are missing.
const CALENDAR_SETUP_FIELDS = [
  'Calendar',
  'Availability',
  'Quest Bio',
  'Executive Bio',
  'Video Call Link',
]

// Links up to this length are shown on one line in the tooltip
const SHORT_LINK_LENGTH = 40

const CoachList = ({ isLoading, coaches }) => {
  const { showNotification } = useContext(NotificationContext)

  const copyVideoCallLink = async (e, link) => {
    // Drop focus so the button's focus ring clears after the click
    e.currentTarget.blur()
    try {
      await navigator.clipboard.writeText(link)
      showNotification('Video call link copied', 'success')
    } catch {
      // Clipboard not available (e.g. insecure context); nothing to show
    }
  }

  if (isLoading) {
    return <ABSpinner />
  }

  const renderStaffCoachIcon = (isStaffCoach = false) => {
    if (!isStaffCoach) return null
    return <StaffCoachIcon classes='staff-coach-table-indicator mb-1' />
  }

  const renderCompletedIcon = () => (
    <svg
      aria-hidden='true'
      focusable='false'
      data-prefix='fas'
      data-icon='check'
      className='svg-inline--fa fa-check fa-w-16 pointer-cursor'
      role='img'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 512 512'
      width='20'
      height='20'>
      <path
        fill='#008000'
        d='M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z'></path>
    </svg>
  )

  const renderUncompletedIcon = () => (
    <svg
      aria-hidden='true'
      focusable='false'
      data-prefix='fas'
      data-icon='exclamation'
      className='svg-inline--fa fa-exclamation fa-w-6 pointer-cursor uncompleted-icon'
      role='img'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 192 512'
      width='20'
      height='20'>
      <path
        fill='#FFA500'
        d='M176 432c0 44.112-35.888 80-80 80s-80-35.888-80-80 35.888-80 80-80 80 35.888 80 80zM25.26 25.199l13.6 272C39.499 309.972 50.041 320 62.83 320h66.34c12.789 0 23.331-10.028 23.97-22.801l13.6-272C167.425 11.49 156.496 0 142.77 0H49.23C35.504 0 24.575 11.49 25.26 25.199z'></path>
    </svg>
  )

  const renderNoSetupIcon = () => (
    <svg
      aria-hidden='true'
      focusable='false'
      className='pointer-cursor'
      role='img'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      width='20'
      height='20'
      fill='none'
      stroke='#dc3545'
      strokeWidth='3'
      strokeLinecap='round'>
      <path d='M6 6l12 12M18 6L6 18' />
    </svg>
  )

  const renderMeetLinkIcon = () => (
    <svg
      aria-hidden='true'
      focusable='false'
      role='img'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 24 24'
      width='20'
      height='20'
      fill='none'
      stroke='#1a73e8'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'>
      <path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' />
      <path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' />
    </svg>
  )

  const renderCalendarSetup = (coach) => {
    const incompletedFields = coach?.calendarSetupIncompletedFields

    // Older backend does not send this field; never treat it as complete
    if (!Array.isArray(incompletedFields)) {
      return '-'
    }

    const videoCallLink = coach?.videoCallLink?.trim()

    return (
      <div className='d-flex align-items-center'>
        {incompletedFields.length === 0 ? (
          renderCompletedIcon()
        ) : (
          <OverlayTrigger
            key='bottom'
            placement='bottom'
            overlay={
              <Tooltip
                id={`tooltip-calendar-setup-${coach?._id}`}
                className='font-open-sans'>
                {incompletedFields.join(' , ')}
              </Tooltip>
            }>
            {incompletedFields.length === CALENDAR_SETUP_FIELDS.length
              ? renderNoSetupIcon()
              : renderUncompletedIcon()}
          </OverlayTrigger>
        )}
        {videoCallLink && (
          <>
            <OverlayTrigger
              key='bottom'
              placement='bottom'
              overlay={
                <Tooltip
                  id={`tooltip-video-call-link-${coach?._id}`}
                  className={`font-open-sans ${
                    videoCallLink.length <= SHORT_LINK_LENGTH
                      ? 'coach-meet-link-tooltip-nowrap'
                      : 'word-breakall'
                  }`}>
                  {videoCallLink}
                </Tooltip>
              }>
              <button
                type='button'
                className='btn btn-link p-0 ml-2 pointer-cursor'
                aria-label='Copy video call link'
                onClick={(e) => copyVideoCallLink(e, videoCallLink)}>
                {renderMeetLinkIcon()}
              </button>
            </OverlayTrigger>
          </>
        )}
      </div>
    )
  }

  return (
    <Container fluid className='custom-offset-lg-1 custom-col-lg-12 px-0'>
      <Row>
        <Col className='coaches-list'>
          <>
            <p className='text-center'>
              Total Remaining Capacity for All coaches:{' '}
              {coaches.totalCapacity - coaches.occupiedCapacity}(
              {coaches.totalCapacity})
            </p>
            <Table hover responsive className='custom-table'>
              <thead>
                <tr>
                  <th width='2%'>#</th>
                  <th width='18%'>Full Name</th>
                  <th width='14%'>Sign Up Date</th>
                  <th width='12%'>Assigned Client</th>
                  <th width='14%'>Remaining Capacity</th>
                  <th width='20%'>Profile Status</th>
                  <th width='20%'>Calendar Setup</th>
                </tr>
              </thead>
              <tbody className='font-16'>
                {coaches?.coaches?.length === 0 ? (
                  <tr>
                    <td className='text-center' colSpan='7'>
                      No coaches found for given criteria
                    </td>
                  </tr>
                ) : (
                  coaches?.coaches?.map((coach, index) => (
                    <tr key={index}>
                      <td width='2%'>{index + 1}</td>
                      <td width='18%'>
                        <div className='d-flex flex-column'>
                          <Link
                            to={`/admin/coaches/${coach?._id}`}
                            className='text-dark-600'>
                            <Row className='d-flex'>
                              <Col>
                                <span className='word-breakall'>
                                  {coach?.fullName}
                                </span>
                                {renderStaffCoachIcon(coach?.isStaffCoach)}
                              </Col>
                            </Row>
                          </Link>
                          <Link
                            to={`/admin/coaches/${coach?._id}`}
                            className='text-dark-600'>
                            {coach?.email}
                          </Link>
                        </div>
                      </td>
                      <td width='14%'>{formatDate(coach?.createdAt)}</td>
                      <td width='12%'>{coach?.occupiedCapacity}</td>
                      {Math.abs(coach?.capacity - coach?.totalClients) ? (
                        <td width='14%'>
                          {coach?.occupiedCapacity}({coach?.capacity})
                        </td>
                      ) : (
                        <td width='14%'>Not Specified</td>
                      )}
                      <td width='20%'>
                        {coach?.profileCompleted ? (
                          renderCompletedIcon()
                        ) : (
                          <OverlayTrigger
                            key='bottom'
                            placement='bottom'
                            overlay={
                              <Tooltip
                                id={`tooltip-coach-incompletedFields`}
                                className='font-open-sans'>
                                {coach?.incompletedFields.join(' , ')}
                              </Tooltip>
                            }>
                            {renderUncompletedIcon()}
                          </OverlayTrigger>
                        )}
                      </td>
                      <td width='20%'>{renderCalendarSetup(coach)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            {/* <p className='pt-4 mb-1'>
              * Only <b>EVOLUTION</b> clients are considered in calculating
              capacity.
            </p> */}
            <p className='pt-4 mb-1'>
              * Not Specified means coach has not filled their profile yet.
            </p>
          </>
        </Col>
      </Row>
    </Container>
  )
}

export default CoachList
